/**
 * Composer prompt-history — the PURE half of the promptbox history
 * navigation (`ai-docs/lexide-promptbox-history-nav-miniplan.md`).
 *
 * Two exports, both IO-free so the decision table is testable without a
 * DOM or a store:
 *
 * - `pushPrompt` — the MRU reducer (trim, skip blank, collapse
 *   consecutive duplicates, cap).
 * - `resolveHistoryIntent` — decides what an ArrowUp / ArrowDown /
 *   Escape MEANS given the composer's live state. The component maps
 *   the returned intent to state changes; it never re-derives the
 *   conditions.
 *
 * ⚠ LINE-BASED, not position-based — `up-line-or-history` semantics
 * (zsh's name for it), RULED by the operator 2026-08-17 after the first
 * cut required TWO presses per item: recall left the caret at the END,
 * so the next ArrowUp spent itself moving the caret home. The rule now:
 * ArrowUp is history when the collapsed caret sits anywhere on the
 * FIRST LINE (an empty box trivially qualifies); ArrowDown is history
 * only while navigating, with the collapsed caret on the LAST LINE. On
 * any other line the arrows keep their native caret job — so a
 * multiline draft is still fully arrow-navigable, and consecutive
 * presses walk one history item each on the (overwhelmingly common)
 * single-line prompt. Everything else returns "none" and the event
 * must NOT be preventDefault-ed.
 *
 * ⚠ IME composition (pt-BR accents, CJK) uses arrow keys to pick
 * candidates — `isComposing` unconditionally suppresses recall.
 *
 * ⚠ The mention picker owns ArrowUp/Down while open (`ChatInput`'s
 * keydown handles it FIRST and returns) — `pickerOpen` is still an
 * input here so the helper states the full truth on its own, rather
 * than being correct only when called from one specific handler.
 */

/** Last-10, per the operator request. */
export const HISTORY_CAP = 10;

/**
 * MRU push. Blank input and an exact repeat of the newest entry leave
 * the list untouched (re-running your last command must not shuffle
 * history — shell behaviour). Older duplicates are left in place: the
 * sequence "A B A" is real usage, not noise.
 */
export function pushPrompt(
  list: readonly string[],
  text: string,
  cap: number = HISTORY_CAP,
): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [...list];
  if (list[0] === trimmed) return [...list];
  return [trimmed, ...list].slice(0, cap);
}

export type HistoryIntent = "older" | "newer" | "exit" | "none";

export interface HistoryIntentInput {
  key: string;
  /** `e.nativeEvent.isComposing` — arrows belong to the IME while true. */
  isComposing: boolean;
  /** Mention picker open — it owns the arrows entirely. */
  pickerOpen: boolean;
  /** Are we currently displaying a recalled entry? */
  navigating: boolean;
  selectionStart: number;
  selectionEnd: number;
  /** The composer's current text — line membership is computed from it. */
  value: string;
}

export function resolveHistoryIntent(input: HistoryIntentInput): HistoryIntent {
  if (input.pickerOpen || input.isComposing) return "none";
  const collapsed = input.selectionStart === input.selectionEnd;
  if (input.key === "ArrowUp") {
    // History iff the caret sits on the FIRST line — otherwise the
    // arrow's native job (move up a line) proceeds.
    const onFirstLine = !input.value.slice(0, input.selectionStart).includes("\n");
    return collapsed && onFirstLine ? "older" : "none";
  }
  if (input.key === "ArrowDown") {
    // Only meaningful while navigating (there is no "newer than the
    // draft"), and only from the LAST line.
    const onLastLine = !input.value.slice(input.selectionEnd).includes("\n");
    return input.navigating && collapsed && onLastLine ? "newer" : "none";
  }
  if (input.key === "Escape") {
    // Only while navigating — an idle Escape belongs to whoever else
    // wants it (dialogs, focus-return contracts), not to us.
    return input.navigating ? "exit" : "none";
  }
  return "none";
}
